'use client';

import { Helmet } from 'react-helmet';
import { Container } from '@mui/material';
import { useEffect, useState } from 'react';
import { getRunTimeEnv } from '../../util/getEnvVariable';
import logger from '../../util/logging.client';

/**
 * @name SweeprContainer - Sweepr Application Component
 * @param {*}props - URL query paramters
 * @returns {JSX.Element} - Returns Sweepr Application in iframe
 */
const SweeprContainer = props => {
  const clientSideEnv = getRunTimeEnv();
  const [widgetStatus, setWidgetStatus] = useState('initial');

  /**
   * @function sweeprCallback
   * @description  Callback function when sweepr troubleshooting is completed
   */
  window.sweeprCallback = () => {
    window.location.href = props?.Source ? props?.Source : '/troubleshooting';
  };

  useEffect(() => {
    try {
      /**
       * @function reInitializeSweeprWidget
       * @description To re-initialize sweepr widget when OTC changes
       */
      const reInitializeSweeprWidget = () => {
        window.sweeprWidgetBuilt = false;
        // eslint-disable-next-line no-undef
        window.SweeprWidget = new SweeprWidget();
      };
      reInitializeSweeprWidget();
    } catch (error) {
      setTimeout(() => {
        logger.log('error', 'Error loading sweepr widget:', error);
      }, 10000);
    }
  }, [props.OTC]); // Re-run this effect whenever OTC changes

  useEffect(() => {
    const maxLoadTime = 15000; // Max wait time for widget to load (in ms)
    let timeoutId;
    /**
     * @function checkWidget
     * @description Verify widget status
     */
    const checkWidget = () => {
      if (typeof window !== 'undefined' && window.SweeprWidget) {
        setWidgetStatus('loaded'); // Widget detected

        // Disable modals when widget is loaded
        if (props.showLoginVerificationModal) {
          props.showLoginVerificationModal.disable();
        }
        if (props.showAccountVerificationModal) {
          props.showAccountVerificationModal.disable();
        }
        props.setErrorCount(0);
        setWidgetStatus('loadedWidget'); // Successfully loaded
        clearTimeout(timeoutId); // Clear the timeout since widget loaded successfully
      } else if (widgetStatus === 'initial' || widgetStatus === 'loading') {
        props.showLoginVerificationModal.disable();
        props.showAccountVerificationModal.disable();
        setWidgetStatus('loading');
        requestAnimationFrame(checkWidget); // Keep checking
        // Widget not loaded yet
      }
    };

    if (widgetStatus === 'initial') {
      // Start checking when component mounts
      checkWidget();

      // Set timeout for error handling after maxLoadTime (10 seconds)
      timeoutId = setTimeout(() => {
        if (widgetStatus !== 'loadedWidget') {
          setWidgetStatus('error');
          logger.log('error', 'Error loading sweepr widget');
          props.setErrorCount(props.errorCount + 1); // Set error count if widget fails to load
        }
      }, maxLoadTime);
    }

    // Cleanup timeout when component is unmounted or when widget is detected
    return () => clearTimeout(timeoutId);
  }, [widgetStatus]);

  return (
    <Container>
      <Helmet>
        <link
          rel='stylesheet'
          type='text/css'
          href={`${clientSideEnv.NEXT_PUBLIC_SWEEPR_WIDGET_CSS}`}
        />
        <script src={`${clientSideEnv.NEXT_PUBLIC_SWEEPR_WIDGET_JS}`} async />
      </Helmet>
      <div
        data-widget-type='modal'
        id='sweepr-widget'
        data-interaction-end-callback='sweeprCallback'
        data-widget-url={`${clientSideEnv.NEXT_PUBLIC_SWEEPR_APPLICATION}`}
        data-support-resources-url={`${clientSideEnv.NEXT_PUBLIC_SUPPORT_REDIRECT_URL}`}
        data-widget-visible='true'
        data-widget-display-home={props?.Utterance ? 'false' : 'true'}
        data-widget-static-size='true'
        class='sweepr-widget type-modal'
        data-widget-otc={props?.OTC}
        data-sweepr-utterance={props?.Utterance}
        style={{ display: 'flex' }}
      ></div>
    </Container>
  );
};

SweeprContainer.displayName = 'SweeprContainer';
export default SweeprContainer;
